import React, { useEffect, useState } from "react";
import { Routes, Route, useParams, NavLink, useNavigate, Navigate } from "react-router-dom";

import { useSelector } from "react-redux";
import { RiErrorWarningLine, RiTimeLine } from "react-icons/ri";

import Loader from "../../../components/loader";
import api from "../../../services/api";

import BudgetUnified from "./budgetUnified";
import Edit from "./edit";
import Billing from "./billing";
import Activities from "./activities";

import Breakdown from "./breakdown";
import Invoice from "./invoice";
import Document from "./document";
import Raw from "./raw";
import Perks from "./perks";
import Team from "./team";
import Backlog from "./backlog";
import Roadmap from "./roadmap";
import Payments from "./payments";
import Tjms from "./tjms";
import Need from "./need";
const ProjectMarkdown = React.lazy(() => import("./markdown"));
import Roadlog from "./roadlog";

const tabItems = [
  { href: "/budget", title: "Budget", roles: ["super_admin", "admin", "client", "user"] },
  { href: "/activities", title: "Activities", roles: ["super_admin", "admin", "client", "user"] },
  { href: "/invoice", title: "Invoices", roles: ["super_admin", "admin", "client", "user"] },
  { href: "/payments", title: "Payments", roles: ["super_admin", "admin", "user"] },
  { href: "/breakdown", title: "Breakdown", roles: ["super_admin", "admin", "user"] },
  { separator: true },
  { href: "/team", title: "Team", roles: ["super_admin", "admin", "client", "user"] },
  { href: "/document", title: "Document", roles: ["super_admin", "admin", "client", "user"] },
  { href: "/markdown", title: "Markdown", roles: ["super_admin", "admin"] },
  { href: "/tjms", title: "TJMs", roles: ["super_admin", "admin", "user"] },
  { href: "/roadlog", title: "Back'N'Road", roles: ["super_admin", "admin", "client", "user"], requiresBacknroad: true },
  { href: "/edit", title: "Edit", roles: ["super_admin", "admin", "user"] },
  { href: "/raw", title: "Raw", roles: ["super_admin", "admin", "user"] },
];

const Project = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [id]);

  async function fetchData() {
    try {
      const { data, ok } = await api.get(`/project/${id}`);
      if (!ok) throw new Error("Project not found");
      setProject(data);
    } catch (e) {
      console.log(e);
      navigate("/project");
    }
  }

  const user = useSelector((state) => state.Auth.user);

  if (!project) return <Loader />;

  return (
    <div className="px-4">
      <div className="my-1 flex items-center justify-between gap-2">
        <nav className="flex-1 rounded-md flex items-center gap-1 border border-neutral-300 bg-white p-1">
          <div className="flex items-center mx-2 flex-shrink-0">
            {project.website ? (
              <a href={project.website} target="_blank" rel="noopener noreferrer">
                <img
                  src={project.logo || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"}
                  alt="projectlogo"
                  className="rounded-md w-5 h-5 bg-gray-50 object-contain mr-2"
                />
              </a>
            ) : (
              <img
                src={project.logo || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"}
                alt="projectlogo"
                className="rounded-md w-5 h-5 bg-gray-50 object-contain mr-2"
              />
            )}

            <h1 className="text-xl font-bold mr-2 truncate">{project.name}</h1>
            <div className="mr-2 flex items-center">
              <LateInvoices project={project} />
            </div>
            <div className="mr-2 flex items-center">
              <Inconsistencies project={project} />
            </div>
          </div>
          <div className="flex items-center gap-1 overflow-x-auto flex-1">
            {tabItems
              .filter((item) => !item.requiresBacknroad || project.disable_backnroad === false)
              .map((item, index) =>
                item.separator ? (
                  <div key={`sep-${index}`} className="mx-2 h-6 w-px flex-shrink-0 bg-neutral-300" />
                ) : (
                  <TabItem key={item.href} href={item.href} title={item.title} roles={item.roles} />
                ),
              )}
          </div>
        </nav>
      </div>
      <div>
        <Routes>
          <Route path="/" element={<Navigate to="budget" replace />} />
          <Route path="/backlog/:tab?" element={<Backlog project={project} setProject={setProject} />} />
          <Route path="/roadlog/:tab?" element={<Roadlog project={project} setProject={setProject} />} />
          <Route path="/budget" element={<BudgetUnified project={project} setProject={setProject} />} />
          <Route path="/budget-normal" element={<BudgetUnified project={project} setProject={setProject} />} />
          <Route path="/budget-admin" element={<BudgetUnified project={project} setProject={setProject} />} />
          <Route path="/edit" element={<Edit project={project} setProject={setProject} />} />
          <Route path="/breakdown" element={<Breakdown project={project} setProject={setProject} />} />
          <Route path="/activities" element={<Activities project={project} setProject={setProject} />} />
          <Route path="/roadmap/*" element={<Roadmap project={project} setProject={setProject} />} />
          <Route path="/invoice" element={<Invoice project={project} setProject={setProject} />} />
          <Route path="/billing" element={<Billing project={project} setProject={setProject} />} />
          <Route path="/document" element={<Document project={project} setProject={setProject} />} />
          <Route path="/markdown" element={["admin", "super_admin"].includes(user?.role) ? <React.Suspense fallback={<Loader />}><ProjectMarkdown key={project._id} project={project} /></React.Suspense> : <Navigate to={`/project/${id}/budget`} replace />} />
          <Route path="/perks" element={<Perks project={project} setProject={setProject} />} />
          <Route path="/need" element={<Need project={project} setProject={setProject} />} />
          <Route path="/raw" element={<Raw project={project} setProject={setProject} />} />
          <Route path="/team" element={<Team project={project} setProject={setProject} />} />
          <Route path="/payments" element={<Payments project={project} />} />
          <Route path="/tjms" element={<Tjms project={project} setProject={setProject} />} />
          {/* <Route path="/nps" element={<Nps project={project} setProject={setProject} />} /> */}
        </Routes>
      </div>
    </div>
  );
};

const TabItem = ({ href, title, roles }) => {
  const { id } = useParams();
  const { user } = useSelector((state) => state.Auth);

  if (!roles.includes(user.role)) return <div />;

  return (
    <NavLink to={`/project/${id}${href}`} className={({ isActive }) => (isActive ? "blue-btn" : "transparent-btn cursor-pointer relative rounded flex items-center")}>
      <div className="flex items-center gap-2 truncate">{title}</div>
    </NavLink>
  );
};

const LateInvoices = ({ project }) => {
  const [invoices, setInvoices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const { data } = await api.post("/invoice/search", { projectId: project._id, late_invoice: true, received: "no" });
    setInvoices(data);
  };

  if (!invoices.length) return null;

  return (
    <div className="relative inline-block">
      <div className="relative cursor-pointer group">
        <RiTimeLine className="h-6 w-6 text-red-500" />
        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">{invoices.length}</div>

        {/* Tooltip */}
        <div className="absolute left-full ml-2 mt-20 -translate-y-1/2 w-96 p-2 bg-gray-900 text-white text-sm rounded-lg z-50 invisible group-hover:visible">
          <div className="flex flex-col gap-1">
            {invoices.map((invoice) => (
              <div key={invoice._id}>
                Invoice <strong>{invoice.name}</strong> of <strong>{invoice.total.toLocaleString("fr", { style: "currency", currency: "EUR" })}</strong> is{" "}
                <strong>{invoice.late}</strong> days late
              </div>
            ))}
            <button onClick={() => navigate(`/project/${project._id}/invoice`)} className="mt-2 w-full text-center px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded">
              View invoices
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Inconsistencies = ({ project }) => {
  const user = useSelector((state) => state.Auth.user);
  const navigate = useNavigate();
  if (user.role !== "admin") return null;
  const missingFields = [];

  if (!project.name) missingFields.push("Client name missing");
  if (!project.clientAddress) missingFields.push("Client's address missing");
  if (!project.clientVat) missingFields.push("Client's VAT missing");
  if (!project.clientSiret) missingFields.push("Client's SIREN missing");

  if (!missingFields.length) return null;

  return (
    <div className="relative inline-block ">
      <div className="relative cursor-pointer group">
        <RiErrorWarningLine className="h-6 w-6 text-red-500" />
        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">{missingFields.length}</div>

        {/* Tooltip */}
        <div className="absolute left-full -mt-8 pl-3 invisible group-hover:visible z-50">
          <div className="w-64 p-2 bg-gray-900 text-white text-sm rounded-lg">
            <div className="flex flex-col gap-1">
              {missingFields.map((field, index) => (
                <div key={index}>{field}</div>
              ))}
              <button onClick={() => navigate(`/project/${project._id}/edit`)} className="mt-2 w-full text-center px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded">
                Edit project
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Project;
